import { useEffect, useRef, useState } from "react";
import "../styles/Timeline.css";
import "../styles/Visualization.css";
import { TimelineProps } from "../interfaces/Timeline";
import MiniPatternView from "./MiniPatternView";
import Callout from "./Callout";
import { Arrow90degUp, ArrowUp, PlusCircle } from "react-bootstrap-icons";
import SetBarAndBeatModal from "./SetBarAndBeatModal";
import { ReassuranceModal } from "./ReassuranceModal";
import { Dance } from "../interfaces/Choreography";
import { schemeSet3 } from "d3-scale-chromatic";
import { current } from "immer";
import NewDanceModal from "./NewDanceModal";
import { ReassuranceModalDance } from "./ReassuranceModalDance";
import UpdateDanceModal from "./UpdateDanceModal";
import { ReassuranceModalRemoveBar } from "./ReassuranceModalRemoveBar";

function TimelineDanceDriven(props: TimelineProps) {
  const divRef = useRef<HTMLDivElement>(null);

  //Data for the SetBarAndBeatModal
  const [show, setShow] = useState<boolean>(false);
  const handleClose = () => setShow(false);
  const handleShow = () => {
    if (props.editingModeActive) setShow(true);
  };
  const [selectedBarModal, setSelectedBarModal] = useState<number>(-1);
  const [selectedBeatModal, setSelectedBeatModal] = useState<number>(-1);
  const [patternNameModal, setPatternNameModal] = useState<string>("");

  //If reassurance model (for pattern deletion) should be shown
  const [showReassuranceModal, setShowReassuranceModal] =
    useState<boolean>(false);

  //Data for the new dance modal
  const [showUpdateDanceModal, setShowUpdateDanceModal] =
    useState<boolean>(false);
  const [showNewDanceModal, setShowNewDanceModal] = useState<boolean>(false);
  const [newDanceName, setNewDanceName] = useState<string>("");
  const [newBarNumber, setNewBarNumber] = useState<number>(8);
  const [newBeatsPerBarNumber, setNewBeatsPerBarNumber] = useState<number>(8);

  //If reassurance model (for dance deletions or updates) should be shown
  const [showReassuranceModalDance, setShowReassuranceModalDance] =
    useState<boolean>(false);
  const [showReassuranceModalRemoveBar, setShowReassuranceModalRemoveBar] =
    useState<boolean>(false);

  const [barToRemove, setBarToRemove] = useState<number>(-1);
  const [hoveredBar, setHoveredBar] = useState<number>(-1);

  //Data for the update dance modal
  const [selectedDance, setSelectedDance] = useState<number>(0);
  const [updatedDanceName, setUpdatedDanceName] = useState<string>("");

  //Constant bar and beat widths
  const barWidth: number = 200;
  const beatWidth: number = barWidth / 8;

  const quarterTickWidth = 3;
  const eighthTickWidth = 1;

  const [selectedBar, setSelectedBar] = useState<number>(-1);
  const [selectedBeat, setSelectedBeat] = useState<number>(-1);

  const [hoveredNewDanceArea, setHoveredNewDanceArea] =
    useState<boolean>(false);

  const [isCalloutVisible, setIsCalloutVisible] = useState<boolean>(false);
  const [calloutTransform, setCalloutTransform] = useState<string>("");

  const [
    newIntermediatePatternMarkerVisible,
    setNewIntermediatePatternMarkerVisible,
  ] = useState<boolean>(true);

  const [beatMapValues, setBeatMapValues] = useState<number[][]>([]);
  const [quarterTickMapValues, setQuarterTickMapValues] = useState<number[][]>(
    []
  );
  const [eighthTickMapValues, setEightTickMapValues] = useState<number[][]>([]);

  useEffect(() => {
    let localHighestBarNumber = 0;
    props.choreo.dances.forEach((dance: Dance) => {
      localHighestBarNumber += dance.bars;
    });

    //Fill timeline map values
    let temp: number[] = [];
    for (let i = 0; i < localHighestBarNumber; i++) {
      temp.push(i);
    }
    props.setHighestBarNumber(localHighestBarNumber);
    //console.log(localHighestBarNumber)
    props.setTimelineMapValues(temp);
    //console.log(temp)

    //Fill beat map values
    let beatBuffer: number[][] = [];
    let quarterBuffer: number[][] = [];
    let eighthBuffer: number[][] = [];

    props.choreo.dances.forEach((dance: Dance) => {
      for (let i = 0; i < dance.bars; i++) {
        let localBeatBuffer: number[] = [];
        let localQuarterBuffer: number[] = [];
        let localEighthBuffer: number[] = [];

        for (let j = 0; j < dance.beatsPerBar; j++) {
          localBeatBuffer.push(j);
          if (j < dance.beatsPerBar / 2) localQuarterBuffer.push(j);
          if (j % 2 === 1) localEighthBuffer.push(j);
        }

        beatBuffer.push(localBeatBuffer);
        quarterBuffer.push(localQuarterBuffer);
        eighthBuffer.push(localEighthBuffer);
      }
    });
    setBeatMapValues(beatBuffer);
    //console.log(beatBuffer)
    setQuarterTickMapValues(quarterBuffer);
    setEightTickMapValues(eighthBuffer);
  }, [props.choreo]);

  useEffect(() => {
    setPatternNameModal(props.choreo.patterns[props.selectedPattern].name);
  }, [props.selectedPattern, props.choreo]);

  /**
   * Callback function that scrolls the timeline to the correct position, if a new pattern gets selected.
   * The method tries to put the selected pattern in the center of the screen. This is not possible, if
   * there would be parts missing on the left/right side (e.g. if the centering would require the bar -1).
   */
  useEffect(() => {
    if (divRef.current) {
      let scrollX = props.choreo.patterns[props.selectedPattern].bar * barWidth;
      if (scrollX > window.innerWidth / 2) {
        scrollX -= window.innerWidth / 2;
      }
      divRef.current.scrollTo(scrollX, 0);
    }
  }, [props.selectedPattern]);

  function applyModalValues() {
    props.setBarAndBeat(
      props.selectedPattern,
      selectedBarModal,
      selectedBeatModal,
      props.highestBarNumber
    );
    props.setPatternName(props.selectedPattern, patternNameModal);
    handleClose();
  }

  function updateDanceByModal() {
    props.updateDanceName(selectedDance, updatedDanceName);
  }

  function showCallout(xPosition: number, yPosition: number) {
    setIsCalloutVisible(false);
    setCalloutTransform(`translate(${xPosition}, ${yPosition})`);
    setIsCalloutVisible(true);
  }

  function hideCallout() {
    setIsCalloutVisible(false);
  }

  function getMiniPatternView(barNumber: number, beatNumber: number) {
    let hoveredPattern = -1;
    props.choreo.patterns.forEach((pattern) => {
      if (pattern.beat === beatNumber && pattern.bar === barNumber) {
        hoveredPattern = pattern.id;
        return;
      }
    });

    if (hoveredPattern === -1) {
      return undefined;
    }

    return (
      <MiniPatternView
        danceFloor={props.choreo.danceFloor}
        pattern={props.choreo.patterns[hoveredPattern]}
        gentlemanColor={props.gentlemanColor}
        ladyColor={props.ladyColor}
        coupleColor={props.coupleColor}
        colorCodedCouples={props.colorCodedCouples}
        coupleColors={props.coupleColors}
      />
    );
  }

  //Height of the complete timeline SVG
  function getTimelineHeight() {
    return 80;
  }

  //Width of the complete timeline SVG
  function getTimelineWidth() {
    let currentWidth: number = 0;
    props.choreo.dances.forEach((dance: Dance) => {
      currentWidth += dance.bars * dance.beatsPerBar * beatWidth;
    });
    //Include the new-dance-button if editing mode is active
    if (props.editingModeActive) {
      currentWidth += 8 * beatWidth;
    }

    return currentWidth;
  }

  //Get the x-position of a segment in the name bar for a given index (the index is the position of the dance in the dances array).
  function getNameBarX(danceIndex: number) {
    let currentX: number = 0;
    for (let i = 0; i < danceIndex; i++) {
      const currentDance = props.choreo.dances[i];
      currentX += currentDance.bars * currentDance.beatsPerBar * beatWidth;
    }
    return currentX;
  }

  function getBarFill(barNumber: number, isInChoreo: boolean) {
    if (isInChoreo) {
      return "#e1e1fc";
    }
    if (barNumber % 2 < 1) {
      return "#eeeeee";
    }

    return "#dddddd";
  }

  function getBeatFill(bar: number, beat: number) {
    if (bar === selectedBar && beat === selectedBeat) {
      return "#bbbbbb";
    }

    let isInChoreo = false;
    props.choreo.patterns.forEach((pattern) => {
      if (pattern.beat === beat && pattern.bar === bar) {
        isInChoreo = true;
        return;
      }
    });

    return getBarFill(bar, isInChoreo);
  }

  function getBeatStrokeWidth(bar: number, beat: number) {
    if (bar === selectedBar && beat === selectedBeat) {
      return "1";
    }

    const selectedPattern = props.choreo.patterns[props.selectedPattern];
    if (bar === selectedPattern.bar && beat === selectedPattern.beat) {
      return "1";
    }
    return "0";
  }

  function getBeatStroke(bar: number, beat: number) {
    let strokeWidth = Number(getBeatStrokeWidth(bar, beat));
    if (strokeWidth === 0) {
      return "none";
    } else {
      return "black";
    }
  }

  function getBeatZIndex(bar: number, beat: number) {
    let strokeWidth = Number(getBeatStrokeWidth(bar, beat));
    if (strokeWidth === 0) {
      return 0;
    } else {
      return 1;
    }
  }

  function getBarWidth(barIndex: number) {
    let width: number = 0;
    let currentBar: number = 0;

    for (let i = 0; i < props.choreo.dances.length; i++) {
      const currentDance = props.choreo.dances[i];
      currentBar += currentDance.bars;

      if (currentBar >= barIndex) return currentDance.beatsPerBar * beatWidth;
    }

    return width;
  }

  function getBarX(barIndex: number) {
    let currentX: number = 0;
    let currentBar: number = 0;

    for (let i = 0; i < props.choreo.dances.length; i++) {
      const currentDance = props.choreo.dances[i];
      currentBar += currentDance.bars;

      if (currentBar < barIndex) {
        currentX += currentDance.bars * currentDance.beatsPerBar * beatWidth;
      } else {
        const barsToGo = currentDance.bars - (currentBar - barIndex);
        currentX += barsToGo * currentDance.beatsPerBar * beatWidth;
        break;
      }
    }

    return currentX;
  }

  function getBeatNumberInBar(barIndex: number) {
    let beatNumber: number = 0;
    let currentBar: number = 0;

    for (let i = 0; i < props.choreo.dances.length; i++) {
      const currentDance = props.choreo.dances[i];
      currentBar += currentDance.bars;

      if (currentBar >= barIndex) return currentDance.beatsPerBar;
    }

    beatNumber = 1;

    return beatNumber;
  }

  function mouseEnterBeat(barNumber: number, beatNumber: number) {
    setSelectedBeat(beatNumber);
    setSelectedBar(barNumber);

    let found = false;
    props.choreo.patterns.forEach((pattern) => {
      if (pattern.bar === barNumber && pattern.beat === beatNumber) {
        found = true;
        return;
      }
    });
    if (found) {
      setNewIntermediatePatternMarkerVisible(false);
    } else {
      setNewIntermediatePatternMarkerVisible(true);
    }

    const miniPatternView = getMiniPatternView(barNumber, beatNumber);
    if (miniPatternView !== undefined) {
      showCallout(
        getBarX(barNumber) +
          (beatNumber * getBarWidth(barNumber)) /
            getBeatNumberInBar(barNumber) +
          32,
        -5
      );
    }
  }

  function mouseLeaveBeat() {
    setSelectedBeat(-1);
    setSelectedBar(-1);
    hideCallout();
  }

  function mouseClickBeat(barNumber: number, beatNumber: number) {
    //if (props.editingModeActive) {
    //  props.promoteIntermediatePattern(barNumber, beatNumber);
    //}

    if (
      barNumber === props.choreo.patterns[props.selectedPattern].bar &&
      beatNumber === props.choreo.patterns[props.selectedPattern].beat
    ) {
      setSelectedBarModal(props.choreo.patterns[props.selectedPattern].bar);
      setSelectedBeatModal(props.choreo.patterns[props.selectedPattern].beat);
      handleShow();
      return;
    }

    let searchedPatternId = -1;

    props.choreo.patterns.forEach((pattern) => {
      if (pattern.bar === barNumber && pattern.beat === beatNumber) {
        searchedPatternId = pattern.id;
      }
    });
    if (searchedPatternId > -1) {
      props.setSelectedPattern(searchedPatternId);
    } else {
      if (props.editingModeActive) {
        props.addIntermediatePattern(barNumber, beatNumber);
      }
    }

    setSelectedBarModal(props.choreo.patterns[props.selectedPattern].bar);
    setSelectedBeatModal(props.choreo.patterns[props.selectedPattern].beat);
  }

  return (
    <div
      ref={divRef}
      className="not-for-mobile my-footer timeline adaptive-timeline"
    >
      <svg width={getTimelineWidth()} height={getTimelineHeight()}>
        <g id="name-bar">
          {props.choreo.dances.map((dance: Dance, i: number) => (
            <g
              id={`name-bar-segment-${dance.name}`}
              key={`name-bar-segment-${dance.name}`}
            >
              <rect
                x={getNameBarX(i)}
                y={0.8 * getTimelineHeight()}
                width={dance.bars * dance.beatsPerBar * beatWidth}
                height={getTimelineHeight() / 5}
                fill={schemeSet3[i % 9]}
                className="pointer-cursor"
                onClick={() => {
                  if (props.editingModeActive) {
                    setSelectedDance(i);
                    setUpdatedDanceName(dance.name);
                    setShowUpdateDanceModal(true);
                  }
                }}
              />
              <text
                x={getNameBarX(i) + 10}
                y={getTimelineHeight() - 3}
                fontSize="14"
                className="unselectable-text pointer-cursor"
                onClick={() => {
                  if (props.editingModeActive) {
                    setSelectedDance(i);
                    setUpdatedDanceName(dance.name);
                    setShowUpdateDanceModal(true);
                  }
                }}
              >
                {dance.name}
              </text>
            </g>
          ))}
        </g>
        <g>
          {props.timelineMapValues.map((barNumber: number) => (
            <g
              transform={`translate(${getBarX(barNumber)},${0})`}
              key={String(barNumber)}
            >
              <rect
                x={0}
                y={0}
                width={getBarWidth(barNumber)}
                height={0.8 * getTimelineHeight()}
                strokeWidth="1"
                fill="#e5e5e5"
                className="pointer-cursor"
                onClick={(e) => {
                  if (props.editingModeActive) {
                    setBarToRemove(barNumber);
                    setShowReassuranceModalRemoveBar(true);
                  }
                }}
                onMouseEnter={() => setHoveredBar(barNumber)}
                onMouseLeave={() => setHoveredBar(-1)}
              />

              {beatMapValues[barNumber].map((beatNumber) => (
                <rect
                  x={
                    (beatNumber * getBarWidth(barNumber)) /
                    getBeatNumberInBar(barNumber)
                  }
                  y={0}
                  width={getBarWidth(barNumber) / getBeatNumberInBar(barNumber)}
                  height={0.8 * getTimelineHeight() - 18}
                  stroke={getBeatStroke(barNumber, beatNumber)}
                  fill={getBeatFill(barNumber, beatNumber)}
                  strokeWidth={getBeatStrokeWidth(barNumber, beatNumber)}
                  onMouseEnter={() => mouseEnterBeat(barNumber, beatNumber)}
                  onMouseLeave={() => mouseLeaveBeat()}
                  onClick={() => mouseClickBeat(barNumber, beatNumber)}
                  className="pointer-cursor"
                  key={String(beatNumber)}
                  z={getBeatZIndex(barNumber, beatNumber)}
                />
              ))}

              {quarterTickMapValues[barNumber].map((i) => (
                <line
                  x1={
                    (i * getBarWidth(barNumber)) /
                    (0.5 * getBeatNumberInBar(barNumber))
                  }
                  y1={0}
                  x2={
                    (i * getBarWidth(barNumber)) /
                    (0.5 * getBeatNumberInBar(barNumber))
                  }
                  y2={getTimelineHeight() / 8}
                  stroke="black"
                  strokeWidth={quarterTickWidth}
                  key={String(i)}
                />
              ))}

              {eighthTickMapValues[barNumber].map((i) => (
                <line
                  x1={
                    (i * getBarWidth(barNumber)) / getBeatNumberInBar(barNumber)
                  }
                  y1={0}
                  x2={
                    (i * getBarWidth(barNumber)) / getBeatNumberInBar(barNumber)
                  }
                  y2={getTimelineHeight() / 12}
                  stroke="black"
                  strokeWidth={eighthTickWidth}
                  key={String(i)}
                />
              ))}

              <line
                x1={getBarWidth(barNumber)}
                y1={0.8 * getTimelineHeight()}
                x2={getBarWidth(barNumber)}
                y2={0.8 * getTimelineHeight() - 18}
                stroke="black"
              ></line>

              <text
                x={getBarWidth(barNumber) / 2 - 10}
                y={0.8 * getTimelineHeight() - 5}
                className="unselectable-text pointer-cursor"
                fontSize="10"
                onClick={(e) => {
                  if (props.editingModeActive) {
                    setBarToRemove(barNumber);
                    setShowReassuranceModalRemoveBar(true);
                  }
                }}
                onMouseEnter={() => setHoveredBar(barNumber)}
                onMouseLeave={() => setHoveredBar(-1)}
              >
                {barNumber === hoveredBar && props.editingModeActive
                  ? "🗑"
                  : barNumber + 1}
              </text>
            </g>
          ))}
        </g>
        <g
          transform={`translate(${
            getBarX(selectedBar) + beatWidth * selectedBeat
          },${0})`}
        >
          <circle
            cx={beatWidth / 2}
            cy={0.4 * getTimelineHeight() - 5}
            r="5"
            fill="black"
            visibility={
              newIntermediatePatternMarkerVisible && props.editingModeActive
                ? "visible"
                : "hidden"
            }
            id="marker"
            className="marker pointer-cursor"
            onMouseEnter={() => mouseEnterBeat(selectedBar, selectedBeat)}
            onMouseLeave={() => mouseLeaveBeat()}
            onClick={() => mouseClickBeat(selectedBar, selectedBeat)}
          />
        </g>
        {props.editingModeActive && (
          <g
            transform={`translate(${getBarX(props.highestBarNumber)},${0})`}
            id="new-dance-button"
          >
            <rect
              x={0}
              y={0}
              width={barWidth}
              height={getTimelineHeight()}
              fill={
                hoveredNewDanceArea
                  ? "#bbbbbb"
                  : props.timelineMapValues.length % 2 !== 0
                  ? "#eeeeee"
                  : "#dddddd"
              }
              strokeWidth="1"
              id="newDanceField"
              onMouseEnter={() => setHoveredNewDanceArea(true)}
              onMouseLeave={() => setHoveredNewDanceArea(false)}
              onClick={() => setShowNewDanceModal(true)}
            />

            <PlusCircle
              fontSize={!hoveredNewDanceArea ? 24 : 28}
              x={!hoveredNewDanceArea ? barWidth / 2 - 12 : barWidth / 2 - 14}
              y={
                !hoveredNewDanceArea
                  ? getTimelineHeight() / 2 - 12
                  : getTimelineHeight() / 2 - 14
              }
              onMouseEnter={() => setHoveredNewDanceArea(true)}
              onMouseLeave={() => setHoveredNewDanceArea(false)}
              onClick={() => props.addNewBar()}
            />
          </g>
        )}
        {props.choreo.patterns.map((pattern) => (
          <g
            transform={`translate(${
              pattern.id < 10
                ? getBarX(pattern.bar) + pattern.beat * beatWidth + 8
                : getBarX(pattern.bar) + pattern.beat * beatWidth + 3
            },${-5})`}
            key={String(pattern.id)}
          >
            <text
              x={0}
              y={
                props.choreo.patterns[props.selectedPattern].bar ===
                  pattern.bar &&
                props.choreo.patterns[props.selectedPattern].beat ===
                  pattern.beat &&
                props.editingModeActive
                  ? getTimelineHeight() / 3
                  : getTimelineHeight() / 2.5
              }
              className="unselectable-text pointer-cursor"
              fontWeight={
                props.selectedPattern === pattern.id ? "bold" : "normal"
              }
              onMouseEnter={() => mouseEnterBeat(pattern.bar, pattern.beat)}
              onMouseLeave={() => mouseLeaveBeat()}
              onClick={() => mouseClickBeat(pattern.bar, pattern.beat)}
            >
              {pattern.id}
            </text>
          </g>
        ))}
        {props.editingModeActive &&
          props.choreo.patterns.map((pattern) => (
            <g
              transform={`translate(${
                getBarX(pattern.bar) + pattern.beat * beatWidth + 3
              },${-5})`}
              key={String(pattern.id)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                x={0}
                y={getTimelineHeight() / 2.5}
                width="16"
                height="16"
                visibility={
                  props.choreo.patterns[props.selectedPattern].bar ===
                    pattern.bar &&
                  props.choreo.patterns[props.selectedPattern].beat ===
                    pattern.beat
                    ? "visible"
                    : "hidden"
                }
                fill="currentColor"
                className="bi bi-pencil"
                viewBox="0 0 16 16"
                onMouseEnter={() => mouseEnterBeat(pattern.bar, pattern.beat)}
                onMouseLeave={() => mouseLeaveBeat()}
                onClick={() => mouseClickBeat(pattern.bar, pattern.beat)}
              >
                <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z" />
              </svg>
            </g>
          ))}
        {props.choreo.patterns.map((pattern) => (
          <g key={String(pattern.id)}>
            {pattern.intermediatePatterns.map((intermediatePattern, i) => (
              <g
                transform={`translate(${
                  getBarX(intermediatePattern.bar) +
                  intermediatePattern.beat * beatWidth
                },${0})`}
                key={String(pattern.id + "." + i)}
              >
                <circle
                  cx={beatWidth / 2}
                  cy={0.4 * getTimelineHeight() - 5}
                  r="5"
                />
                {false && props.editingModeActive && (
                  <ArrowUp
                    x={beatWidth / 6}
                    y={0.1 * getTimelineHeight() - 5}
                    visibility={
                      selectedBar === intermediatePattern.bar &&
                      selectedBeat === intermediatePattern.beat
                        ? "visible"
                        : "hidden"
                    }
                  />
                )}
              </g>
            ))}
          </g>
        ))}
        ;
        <g id="callout-group">
          {isCalloutVisible && (
            <Callout
              transform={calloutTransform}
              width={64}
              height={64}
              child={getMiniPatternView(selectedBar, selectedBeat)}
            />
          )}
        </g>
      </svg>

      <SetBarAndBeatModal
        show={show}
        handleClose={handleClose}
        selectedBarModal={selectedBarModal}
        setSelectedBarModal={setSelectedBarModal}
        selectedBeatModal={selectedBeatModal}
        setSelectedBeatModal={setSelectedBeatModal}
        highestBarNumber={props.highestBarNumber}
        setShowReassuranceModal={setShowReassuranceModal}
        applyModalValues={applyModalValues}
        getBeatNumberInBar={getBeatNumberInBar}
        patternNameModal={patternNameModal}
        setPatternNameModal={setPatternNameModal}
        removeAllIntermediatePatterns={props.removeAllIntermediatePatterns}
        selectedPattern={props.selectedPattern}
      />

      <NewDanceModal
        showNewDanceModal={showNewDanceModal}
        setShowNewDanceModal={setShowNewDanceModal}
        newDanceName={newDanceName}
        setNewDanceName={setNewDanceName}
        newBarNumber={newBarNumber}
        setNewBarNumber={setNewBarNumber}
        newBeatsPerBarNumber={newBeatsPerBarNumber}
        setNewBeatsPerBarNumber={setNewBeatsPerBarNumber}
        addNewDance={props.addNewDance}
      />

      <UpdateDanceModal
        show={showUpdateDanceModal}
        handleClose={() => setShowUpdateDanceModal(false)}
        newDanceName={updatedDanceName}
        setNewDanceName={setUpdatedDanceName}
        updateDanceByModal={updateDanceByModal}
        setShowReassuranceModalDance={setShowReassuranceModalDance}
      />

      <ReassuranceModal
        showReassuranceModal={showReassuranceModal}
        setShowReassuranceModal={setShowReassuranceModal}
        selectedPattern={props.selectedPattern}
        removePattern={props.removePattern}
      />

      <ReassuranceModalRemoveBar
        showReassuranceModal={showReassuranceModalRemoveBar}
        setShowReassuranceModal={setShowReassuranceModalRemoveBar}
        barToRemove={barToRemove}
        removeBar={props.removeBar}
      />

      <ReassuranceModalDance
        showReassuranceModal={showReassuranceModalDance}
        setShowReassuranceModal={() => setShowReassuranceModalDance(false)}
        selectedPattern={props.selectedPattern}
        removeDance={props.removeDance}
        selectedDance={selectedDance}
        choreo={props.choreo}
      />
    </div>
  );
}

export default TimelineDanceDriven;
